import type { ReactNode } from 'react';

import type { StageTone } from '../shared/index.js';

import type { BoardCard, DirectoryUser } from '../api/endpoints.js';
import { Badge } from './ui.js';

export const TONE_DOT: Readonly<Record<StageTone, string>> = Object.freeze({
  neutral: 'bg-muted/40',
  gold: 'bg-gold',
  cyan: 'bg-cyan',
  positive: 'bg-positive',
  caution: 'bg-caution',
  critical: 'bg-critical',
});

export function personLabel(people: ReadonlyMap<string, DirectoryUser>, id: string | null): string {
  if (id === null) return 'Unassigned';
  const person = people.get(id);
  return person === undefined ? 'Someone' : `${person.firstName} ${person.lastName}`;
}

function personInitials(
  people: ReadonlyMap<string, DirectoryUser>,
  id: string | null,
): string {
  if (id === null) return '—';
  const person = people.get(id);
  if (person === undefined) return '?';
  return `${person.firstName.slice(0, 1)}${person.lastName.slice(0, 1)}`.toUpperCase();
}

interface TicketCardProps {
  readonly ticket: BoardCard;
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onOpen: (ticketId: string) => void;
  readonly dragging?: boolean;
}

export function TicketCard({
  ticket,
  people,
  onOpen,
  dragging = false,
}: TicketCardProps): ReactNode {
  return (
    <article
      onClick={() => {
        onOpen(ticket.id);
      }}
      className={`group cursor-grab rounded-panel border border-hairline bg-white p-3.5 text-left shadow-[0_1px_2px_rgb(6_24_61/0.04)] transition hover:-translate-y-px hover:shadow-[0_10px_24px_rgb(6_24_61/0.08)] active:cursor-grabbing dark:border-dark-line dark:bg-dark-panel ${
        dragging ? 'opacity-40' : ''
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[11px] font-bold tracking-[0.06em] text-muted">
          SBG#{ticket.number}
        </span>
        {ticket.priority === 'high' ? <Badge tone="critical">High</Badge> : null}
        {ticket.priority === 'low' ? <Badge tone="neutral">Low</Badge> : null}
      </div>

      <p className="text-[14px] leading-snug font-semibold text-navy dark:text-dark-ink">
        {ticket.title}
      </p>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="truncate text-[12px] text-muted">{ticket.departmentSlug}</span>
        <span
          title={personLabel(people, ticket.assigneeId)}
          className="grid size-6 shrink-0 place-items-center rounded-full bg-navy/8 text-[10.5px] font-bold text-navy dark:bg-white/10 dark:text-dark-ink"
        >
          {personInitials(people, ticket.assigneeId)}
        </span>
      </div>
    </article>
  );
}
